import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { useAppDispatch, useAppSelector } from "store/index";
import { handleUpdateFields, updateProviderCurrency } from "store/gameCenter/providers/slice";
import {
  createProviderCurrencyEditor,
  ProviderCurrencyEditor,
  ProviderCurrencyPatch,
} from "store/gameCenter/providers/providerCurrencyState";
import Loader from "components/global/Loader";
import SwitchBtn from "components/buttons/SwitchBtn";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import styles from "../GameCenterProviderEditModal.module.scss";

interface ProviderCurrenciesTabProps {
  isReadOnly: boolean;
}

const currencyStatusLabel: Record<string, string> = {
  active: "providerCurrencyActive",
  disabled_by_manager: "providerCurrencyDisabledByManager",
  unsupported: "providerCurrencyUnsupported",
  disabled_unsupported: "providerCurrencyDisabledUnsupported",
  not_allowed: "providerCurrencyNotAllowed",
};

const ProviderCurrenciesTab: React.FC<ProviderCurrenciesTabProps> = ({
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payload = useAppSelector((store) => store.providers.payload);
  const [pendingCurrency, setPendingCurrency] = useState("");
  const requestSeqRef = useRef(0);
  const editorRef = useRef<ProviderCurrencyEditor | null>(null);
  if (editorRef.current == null) {
    editorRef.current = createProviderCurrencyEditor({
      providerId: payload?.id,
      rows: payload?.providerCurrencies,
    });
  }
  const editor = editorRef.current;

  useEffect(() => {
    editor.sync({
      providerId: payload?.id,
      rows: payload?.providerCurrencies,
    });
  }, [editor, payload?.id, payload?.providerCurrencies]);

  const refreshProviderCurrencies = async (providerId: number) => {
    try {
      const result = await instance.get(`${apiRoutes.providerItem}/${providerId}`);
      if (editor.providerId !== providerId) {
        return;
      }
      if (result?.data?.providerCurrencies) {
        editor.replaceRows(providerId, result.data.providerCurrencies);
        dispatch(handleUpdateFields(result.data));
        return;
      }
    } catch {
      // Keep the last confirmed rows when the refresh also fails.
    }
    editor.fail(providerId);
  };

  const submitCurrencyPatch = (patch: ProviderCurrencyPatch | null) => {
    if (isReadOnly || !patch) {
      return;
    }
    const providerId = editor.providerId;
    if (providerId == null) {
      return;
    }
    setPendingCurrency(patch.currency);
    const requestSeq = ++requestSeqRef.current;
    void (async () => {
      try {
        const updated = await dispatch(updateProviderCurrency({
          id: providerId,
          currency: patch.currency,
          isActive: patch.isActive,
          managerEnabled: patch.managerEnabled,
          isPriority: patch.isPriority,
          requestSeq,
        })).unwrap();
        const applied = editor.confirmIfCurrent(providerId, updated.state);
        if (!applied && editor.providerId === providerId) {
          await refreshProviderCurrencies(providerId);
        }
      } catch {
        if (editor.providerId === providerId) {
          await refreshProviderCurrencies(providerId);
        }
      } finally {
        if (editor.providerId === providerId) {
          setPendingCurrency("");
        }
      }
    })();
  };

  return (
    <div className={`games-order-list ${styles.currencyTable}`}>
      <div className={`item ${styles.currencyRow}`}>
        <div>{t("currency")}</div>
        <div>{t("status")}</div>
        <div>{t("reason")}</div>
        <div>{t("providerCurrencyManagerControl")}</div>
        <div>{t("priority")}</div>
      </div>
      {(payload?.providerCurrencies || []).map((row) => {
        const priorityEligible = Boolean(row.isActive) && Boolean(row.managerEnabled);
        const controlsLocked = isReadOnly || Boolean(pendingCurrency);
        const priorityDisabled = controlsLocked || (!priorityEligible && !row.isPriority);
        return (
          <div
            className={`item ${styles.currencyRow} ${
              pendingCurrency === row.currency ? "modal-table-processing-row" : ""
            }`}
            key={row.currency}
          >
            <div>{row.currency}</div>
            <div>
              <button
                type="button"
                className={`${styles.currencyStatusButton} badge ${row.isActive ? "badge--success" : "badge--danger"}`}
                aria-pressed={Boolean(row.isActive)}
                aria-label={`${row.currency} ${t("status")}`}
                disabled={controlsLocked}
                onClick={() => {
                  if (isReadOnly) {
                    return;
                  }
                  submitCurrencyPatch(editor.beginStatusToggle(row.currency));
                }}
              >
                {row.isActive ? "On" : "Off"}
              </button>
            </div>
            <div>
              {row.effectiveStatus === "active"
                ? "-"
                : t(currencyStatusLabel[row.effectiveStatus] || row.effectiveStatus)}
            </div>
            <div>
              <SwitchBtn
                isActive={row.managerEnabled}
                disabled={controlsLocked}
                handleClick={() => {
                  if (isReadOnly) {
                    return;
                  }
                  submitCurrencyPatch(editor.beginManagerToggle(row.currency));
                }}
              />
            </div>
            <div className={`${styles.currencyPriority} ${priorityEligible ? "" : styles.currencyPriorityIneligible}`}>
              <Checkbox
                id={`provider-currency-priority-${payload?.id || "provider"}-${row.currency}`}
                value={Boolean(row.isPriority)}
                txt=""
                disabled={priorityDisabled}
                ariaLabel={`${row.currency} ${t("priority")}`}
                onCheck={() => {
                  if (isReadOnly) {
                    return;
                  }
                  submitCurrencyPatch(
                    row.isPriority
                      ? editor.beginPriorityClear(row.currency)
                      : editor.beginPrioritySelect(row.currency),
                  );
                }}
              />
            </div>
            {pendingCurrency === row.currency && (
              <div className="modal-table-processing-spinner">
                <Loader />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProviderCurrenciesTab;
